/**
 * captureGenerationSnapshot — freeze the inputs the AI saw at the
 * moment a summary was generated.
 *
 * The snapshot is stored on the execSummary as `generatedInputs` and
 * read back by useSummaryStaleness to detect what has changed since
 * generation. Comparing current state against the frozen snapshot is
 * strictly more accurate than comparing savedAt timestamps, because
 * it correctly handles:
 *   - user writes a field then deletes it → back to original, no
 *     staleness
 *   - two saves land with the same content → not a change
 *   - HubSpot sync runs but totals didn't move → not a change
 *   - action item toggled done/open/done → only the set membership matters
 *
 * Shape (everything is content-only; no savedAt/by/etc.):
 *   {
 *     weeklySummary: { [goalKey]: { tldr, wins, flags } },
 *     teamUpdates:   { [teamId]: { tldr, wins, flags } },
 *     actionItemIds: [sorted list of OPEN item IDs],
 *     subgoalUpdates:{ [subgoalKey]: update text },
 *     hsTotals:      { newCustRev, designRev, teRev, totalHsRev } | null,
 *   }
 *
 * Empty content is omitted (not stored as empty strings) so the diff
 * can tell "had content before, deleted it now" apart from "had no
 * content either time."
 */
export function captureGenerationSnapshot(meta, goalsData, hsReceipt) {
  const weeklySummary = {}
  for (const [key, v] of Object.entries(meta?.weeklySummary || {})) {
    if (!v) continue
    const tldr = v.tldr || ''
    const wins = v.wins || ''
    const flags = v.flags || ''
    if (!tldr && !wins && !flags) continue
    weeklySummary[key] = { tldr, wins, flags }
  }

  const teamUpdates = {}
  for (const [id, v] of Object.entries(meta?.teamUpdates || {})) {
    if (!v) continue
    const tldr = v.tldr || ''
    const wins = v.wins || ''
    const flags = v.flags || ''
    if (!tldr && !wins && !flags) continue
    teamUpdates[id] = { tldr, wins, flags }
  }

  const actionItemIds = []
  for (const items of Object.values(meta?.actionItems || {})) {
    if (!Array.isArray(items)) continue
    for (const item of items) {
      if (item?.status === 'done') continue
      if (item?.id) actionItemIds.push(item.id)
    }
  }
  actionItemIds.sort()

  const subgoalUpdates = {}
  if (Array.isArray(goalsData)) {
    for (let pi = 0; pi < goalsData.length; pi++) {
      const pillar = goalsData[pi]
      if (!pillar?.goals) continue
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const goal = pillar.goals[gi]
        if (!goal || goal.type === 'section') continue
        // Real code uses both `subgoals` and `subGoals` historically; cover both.
        const subs = goal.subgoals || goal.subGoals || []
        for (let si = 0; si < subs.length; si++) {
          const update = subs[si]?.update
          if (update && String(update).trim()) {
            subgoalUpdates[`p${pi}-g${gi}-s${si}`] = String(update)
          }
        }
      }
    }
  }

  const hsTotals = hsReceipt?.totals
    ? {
        newCustRev: Number(hsReceipt.totals.newCustRev) || 0,
        designRev: Number(hsReceipt.totals.designRev) || 0,
        teRev: Number(hsReceipt.totals.teRev) || 0,
        totalHsRev: Number(hsReceipt.totals.totalHsRev) || 0,
      }
    : null

  return { weeklySummary, teamUpdates, actionItemIds, subgoalUpdates, hsTotals }
}

/**
 * Utility — compare two { tldr, wins, flags } objects, treating
 * undefined as the empty string. Returns true if any field differs.
 */
export function tldrWinsFlagsDiffer(a, b) {
  const A = a || {}
  const B = b || {}
  return (A.tldr || '') !== (B.tldr || '')
    || (A.wins || '') !== (B.wins || '')
    || (A.flags || '') !== (B.flags || '')
}

/**
 * Utility — whether two HS totals objects differ on any monetary line.
 * null/null → false. null/object (or object/null) → true iff the object
 * has any non-zero value (a zero-everything "sync ran but nothing
 * matched" result shouldn't trigger a stale notification).
 */
export function hsTotalsDiffer(current, snapshot) {
  const isEmptyTotals = (t) =>
    !t || (!t.newCustRev && !t.designRev && !t.teRev && !t.totalHsRev)
  if (isEmptyTotals(current) && isEmptyTotals(snapshot)) return false
  if (isEmptyTotals(snapshot)) return !isEmptyTotals(current)
  if (isEmptyTotals(current)) return true // had totals, now gone
  return (current.newCustRev || 0) !== (snapshot.newCustRev || 0)
    || (current.designRev || 0) !== (snapshot.designRev || 0)
    || (current.teRev || 0) !== (snapshot.teRev || 0)
    || (current.totalHsRev || 0) !== (snapshot.totalHsRev || 0)
}
